import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Play,
  X,
  Home as HomeIcon,
  Users,
  TrendingUp,
  HeartHandshake,
  Settings,
  Star,
  Gem,
  GraduationCap,
  Megaphone,
  ClipboardCheck,
  Headphones,
  Search,
  MessagesSquare,
  Rocket,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import FranchiseHeader from '../components/FranchiseHeader';
import Footer from '../components/Footer';
import TerritoryMap from '../components/TerritoryMap';
import useReveal from '../hooks/useReveal';

/* Only 5 image assets in the entire page — cropped from the reference */
const IMG_HERO = '/franchise/01-hero.v3.webp';
const IMG_TEAM = '/franchise/02-team.webp?v=2';
const IMG_COLLAGE = '/franchise/03-collage.v3.webp';
const IMG_BULLDOG = '/franchise/04-bulldog.webp';

/* Data */
const BLG = [
  { n: '01', t: 'Build', icon: HomeIcon, body: 'Create a business with a recognizable brand and a proven operating model.' },
  { n: '02', t: 'Lead',  icon: Users,    body: 'Build a local team and become the trusted name in your market.' },
  { n: '03', t: 'Grow',  icon: TrendingUp, body: 'Expand your operation, your territory, and your impact over time.' },
];

const WHY = [
  { t1: 'A Business',    t2: 'With Purpose',   icon: HeartHandshake, body: 'You’re helping families, executors, seniors, professionals and communities navigate major transitions.' },
  { t1: 'A Model',       t2: 'Built To Scale', icon: Settings,        body: 'Systems, processes, branding, training and support designed to help you operate with confidence.' },
  { t1: 'A Brand People',t2: 'Remember',       icon: Star,            body: 'A revolutionary brand transforming the estate sale industry.' },
];

const BENEFITS = [
  { t: 'Brand',      icon: Gem,             body: 'A differentiated identity built for a modern market.' },
  { t: 'Training',   icon: GraduationCap,   body: 'Comprehensive training to set you up for long-term success.' },
  { t: 'Marketing',  icon: Megaphone,       body: 'Proven marketing tools and strategies to build your local presence.' },
  { t: 'Operations', icon: ClipboardCheck,  body: 'A step-by-step framework to help you run efficiently and profitably.' },
  { t: 'Support',    icon: Headphones,      body: 'Guidance from an experienced team whenever you need it.' },
  { t: 'Community',  icon: Users,           body: 'You’re an owner, not alone. Join a network of driven entrepreneurs.' },
];

const OWNER_IF = [
  'Are you ready to build your own dream?',
  'Are you ready for complete career control?',
  'Are you ready to own a business that belongs to you?',
  'Are you ready for entrepreneurship without reinventing the wheel?',
  'Are you ready to be part of a $250 million industry?',
];

const PATH = [
  { n: '01', t: 'Connect',  icon: Users,          body: 'Tell us about yourself and your goals.' },
  { n: '02', t: 'Discover', icon: Search,         body: 'Learn about the Ionic model and what ownership looks like.' },
  { n: '03', t: 'Meet',     icon: MessagesSquare, body: 'Talk with our team and explore whether we’re a fit.' },
  { n: '04', t: 'Launch',   icon: Rocket,         body: 'Build your Ionic business with our support behind you.' },
];

const GreenGlowRule = ({ className = '' }) => (
  <span
    className={`block h-[3px] rounded-full bg-[color:var(--green)] shadow-[0_0_12px_rgba(25,223,2,0.8)] ${className}`}
  />
);

const Franchise = () => {
  const ref = useReveal({ threshold: 0.06 });
  const [videoOpen, setVideoOpen] = useState(false);

  // Lock body scroll while the video modal is open + close on Escape.
  useEffect(() => {
    if (!videoOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setVideoOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [videoOpen]);

  return (
    <div className="bg-black text-white" data-testid="franchise-page">
      <FranchiseHeader />
      <main ref={ref} className="bg-black franchise-body">

        {/* ========================= HERO ========================= */}
        <section
          id="hero"
          data-testid="franchise-hero"
          className="relative overflow-hidden"
        >
          {/* subtle grid + glow behind */}
          <div className="pointer-events-none absolute -top-24 -left-20 w-[520px] h-[520px] rounded-full bg-[color:var(--green)]/[0.08] blur-[130px]" />
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6 md:pt-10 pb-6 md:pb-10">
            <div className="grid md:grid-cols-[minmax(0,.48fr)_minmax(0,.52fr)] gap-6 md:gap-8 items-center">
              {/* Left */}
              <div className="reveal min-w-0">
                <div className="font-mono text-[11px] md:text-[12px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-3 md:mb-4">
                  Ionic Franchise Opportunities
                </div>
                <h1 className="font-display leading-[0.92] tracking-[-0.01em]">
                  <span className="block text-[36px] sm:text-[48px] md:text-[52px] lg:text-[58px] xl:text-[64px] text-white">
                    Own Your
                  </span>
                  <span className="relative inline-block text-[36px] sm:text-[48px] md:text-[52px] lg:text-[58px] xl:text-[64px] text-[color:var(--green)] drop-shadow-[0_0_18px_rgba(25,223,2,0.35)]">
                    Next Chapter.
                    <span className="absolute left-0 -bottom-2 w-full h-[5px] rounded-full bg-[color:var(--green)] shadow-[0_0_16px_rgba(25,223,2,0.85)]" />
                  </span>
                </h1>
                <p className="mt-6 md:mt-7 text-white/85 text-[14.5px] md:text-[15.5px] leading-[1.55] max-w-[440px]">
                  A proven estate liquidation model.<br />
                  A recognizable brand.<br />
                  A business built around helping people through major life transitions.
                </p>
                <div className="mt-7 md:mt-8 flex flex-wrap gap-3 md:gap-4">
                  <Link
                    to="/franchise-apply"
                    data-testid="hero-own-with-ionic-btn"
                    className="group inline-flex items-center gap-1.5 bg-[color:var(--green)] text-black hover:bg-white font-display uppercase tracking-[0.10em] text-[13px] md:text-[14px] px-6 md:px-7 py-3 md:py-3.5 rounded-md shadow-[0_0_28px_-4px_rgba(25,223,2,0.9)] transition-colors"
                  >
                    Own With Ionic
                    <ChevronRight className="w-4 h-4" strokeWidth={3} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setVideoOpen(true)}
                    data-testid="hero-watch-video-btn"
                    className="group inline-flex items-center gap-2 bg-black text-white hover:text-[color:var(--green)] border-2 border-[color:var(--green)] font-display uppercase tracking-[0.10em] text-[13px] md:text-[14px] px-6 md:px-7 py-3 md:py-3.5 rounded-md shadow-[0_0_18px_-4px_rgba(25,223,2,0.7)] transition-colors"
                  >
                    Watch Video
                    <span className="inline-grid place-items-center w-6 h-6 rounded-full bg-[color:var(--green)] text-black">
                      <Play className="w-3.5 h-3.5" fill="black" strokeWidth={2} />
                    </span>
                  </button>
                </div>
              </div>

              {/* Right — IMAGE 01 */}
              <div className="reveal relative">
                <img
                  src={IMG_HERO}
                  alt="Ionic bulldog mascot in a leather chair with a speech bubble that reads Are You Ready To Join Our Pack"
                  className="w-full h-auto object-cover rounded-md select-none"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================= BLG + TEAM ========================= */}
        <section
          id="build-lead-grow"
          data-testid="build-lead-grow"
          className="relative bg-black"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="grid md:grid-cols-[minmax(0,.5fr)_minmax(0,.5fr)] gap-6 md:gap-8 items-stretch">
              {/* Left — BLG columns */}
              <div className="reveal min-w-0">
                <h2 className="font-display text-[34px] sm:text-[44px] md:text-[48px] lg:text-[52px] leading-none tracking-[-0.01em] mb-8 md:mb-10 whitespace-nowrap">
                  <span className="relative inline-block text-white">
                    Build.
                    <GreenGlowRule className="absolute left-0 -bottom-1 w-[85%]" />
                  </span>{' '}
                  <span className="relative inline-block text-white">
                    Lead.
                    <GreenGlowRule className="absolute left-0 -bottom-1 w-[80%]" />
                  </span>{' '}
                  <span className="text-[color:var(--green)] drop-shadow-[0_0_16px_rgba(25,223,2,0.4)]">
                    Grow.
                  </span>
                </h2>

                <div className="grid grid-cols-3 gap-0">
                  {BLG.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <div
                        key={s.n}
                        className={`pr-4 md:pr-5 ${
                          i !== BLG.length - 1 ? 'border-r border-[color:var(--green)]/50 shadow-[inset_-1px_0_15px_-8px_rgba(25,223,2,0.55)]' : ''
                        } ${i > 0 ? 'pl-4 md:pl-5' : ''}`}
                      >
                        <Icon
                          className="w-9 h-9 md:w-10 md:h-10 text-[color:var(--green)] drop-shadow-[0_0_10px_rgba(25,223,2,0.55)] mb-3"
                          strokeWidth={1.5}
                        />
                        <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/60">
                          {s.n}
                        </div>
                        <h3 className="font-display text-[18px] md:text-[20px] tracking-wide text-white leading-tight">
                          {s.t}
                          <GreenGlowRule className="mt-1 w-8" />
                        </h3>
                        <p className="mt-2 text-white/70 text-[12.5px] leading-[1.45]">
                          {s.body}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <p className="mt-8 md:mt-10 font-display uppercase tracking-[0.02em] text-white text-[17px] sm:text-[20px] md:text-[22px] leading-[1.15]">
                  You Bring The Energy And The Drive.
                  <br />
                  <span className="relative inline-block">
                    We Bring The{' '}
                    <span className="relative inline-block text-white">
                      Playbook.
                      <GreenGlowRule className="absolute left-0 -bottom-1 w-full" />
                    </span>
                  </span>
                </p>
              </div>

              {/* Right — IMAGE 02 (clean team + truck photo, HTML floating testimonial layered on top) */}
              <div className="reveal relative rounded-md overflow-hidden">
                <img
                  src={IMG_TEAM}
                  alt="Ionic Estate Sales team members in branded shirts loading an Ionic-branded truck at a residential estate"
                  className="w-full h-full object-cover select-none"
                  style={{ objectPosition: 'center' }}
                  draggable={false}
                />

                {/* Floating editorial testimonial — translucent dark bubble
                    behind the quote so the copy stays fully readable over
                    the photo. Positioned bottom-left of the image. */}
                <figcaption
                  data-testid="team-testimonial"
                  className="absolute left-4 sm:left-5 md:left-6 bottom-4 sm:bottom-5 md:bottom-6 max-w-[46%] sm:max-w-[42%] md:max-w-[36%] text-left pointer-events-none rounded-2xl bg-black/55 backdrop-blur-sm border border-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9)] px-4 py-3 sm:px-5 sm:py-4 md:px-6 md:py-5"
                  style={{ textShadow: '0 2px 14px rgba(0,0,0,0.95), 0 0 26px rgba(0,0,0,0.7)' }}
                >
                  <span
                    aria-hidden="true"
                    className="block font-display leading-none text-[color:var(--green)] text-[44px] sm:text-[54px] md:text-[64px]"
                    style={{ textShadow: '0 0 26px rgba(25,223,2,0.6), 0 2px 10px rgba(0,0,0,0.85)' }}
                  >
                    &ldquo;
                  </span>
                  <blockquote className="-mt-2 sm:-mt-3 md:-mt-4 font-display uppercase tracking-[0.01em] text-white text-[12.5px] sm:text-[14px] md:text-[15.5px] leading-[1.25]">
                    Ionic took away the stress, gave us peace of mind and helped us begin our next chapter.
                  </blockquote>
                  <cite
                    className="mt-2.5 block font-mono not-italic text-[color:var(--green)] tracking-[0.24em] uppercase text-[12px] md:text-[10px] sm:text-[12px] md:text-[10.5px]"
                    style={{ textShadow: '0 0 12px rgba(25,223,2,0.5), 0 2px 8px rgba(0,0,0,0.85)' }}
                  >
                    &mdash;&nbsp;Sandy A
                  </cite>
                </figcaption>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= WHY IONIC ========================= */}
        <section
          id="why-ionic"
          data-testid="why-ionic"
          className="relative bg-black border-t border-[color:var(--green)]/15"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="reveal font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--green)] mb-6 md:mb-8">
              Why Ionic?
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0">
              {WHY.map((w, i) => {
                const Icon = w.icon;
                return (
                  <article
                    key={w.t1 + w.t2}
                    className={`reveal relative flex gap-5 ${
                      i !== 0 ? 'md:pl-8' : ''
                    } ${i !== WHY.length - 1 ? 'md:pr-8 md:border-r md:border-[color:var(--green)]/50 md:shadow-[inset_-1px_0_15px_-8px_rgba(25,223,2,0.55)]' : ''}`}
                    style={{ transitionDelay: `${i * 90}ms` }}
                  >
                    <div className="shrink-0">
                      <div className="w-16 h-16 md:w-[68px] md:h-[68px] rounded-full border-2 border-[color:var(--green)] grid place-items-center shadow-[0_0_25px_-4px_rgba(25,223,2,0.75)]">
                        <Icon className="w-7 h-7 md:w-8 md:h-8 text-[color:var(--green)]" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-display text-[19px] md:text-[22px] leading-[1.05] tracking-wide text-white">
                        <span className="block">{w.t1}</span>
                        <span className="block">{w.t2}</span>
                      </h3>
                      <p className="mt-3 text-white/70 text-[13px] leading-[1.5] max-w-[220px]">
                        {w.body}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================= EVERYTHING YOU NEED ========================= */}
        <section
          id="everything-you-need"
          data-testid="everything-you-need"
          className="relative bg-black border-t border-[color:var(--green)]/15"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="grid md:grid-cols-[minmax(0,.34fr)_minmax(0,.66fr)] gap-6 md:gap-10 items-start">
              {/* Left — IMAGE 03 */}
              <div className="reveal">
                <img
                  src={IMG_COLLAGE}
                  alt="Photo collage of Ionic franchise owner, a woman working on a laptop, and Ionic team members collaborating"
                  className="w-full h-auto object-cover rounded-md select-none"
                  draggable={false}
                />
              </div>

              {/* Right — 6 benefits grid */}
              <div>
                <div className="reveal font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--green)] mb-3">
                  What You Get
                </div>
                <h2 className="reveal font-display text-[34px] sm:text-[44px] md:text-[52px] leading-[0.98] tracking-[-0.01em] text-white">
                  Everything You Need To <span className="text-[color:var(--green)] drop-shadow-[0_0_16px_rgba(25,223,2,0.4)]">Succeed.</span>
                </h2>

                <div className="mt-6 md:mt-8 grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-6 md:gap-y-8 border-t border-[color:var(--green)]/45 pt-6">
                  {BENEFITS.map((b, i) => {
                    const Icon = b.icon;
                    const midCol = i % 3 === 1;
                    return (
                      <div
                        key={b.t}
                        className={`reveal relative ${midCol ? 'md:border-x md:border-[color:var(--green)]/40 md:px-6' : ''} ${(i > 2) ? 'md:pt-6 md:border-t md:border-[color:var(--green)]/40' : ''}`}
                        style={{ transitionDelay: `${i * 50}ms` }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-6 h-6 text-[color:var(--green)] drop-shadow-[0_0_8px_rgba(25,223,2,0.55)]" strokeWidth={1.5} />
                          <h3 className="font-display text-[15px] md:text-[16px] tracking-wide text-white">
                            {b.t}
                          </h3>
                        </div>
                        <p className="text-white/75 text-[12.5px] leading-[1.5]">
                          {b.body}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= TERRITORY MAP ========================= */}
        <TerritoryMap />

        {/* ========================= OWNER + PATH ========================= */}
        <section
          data-testid="owner-and-path"
          className="relative bg-black border-t border-[color:var(--green)]/15"
        >
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="grid md:grid-cols-[minmax(0,.32fr)_minmax(0,.68fr)] gap-6 md:gap-8 items-stretch">
              {/* Owner panel — green */}
              <div
                id="owner-if"
                data-testid="owner-if"
                className="reveal relative overflow-visible rounded-md text-black p-5 md:p-6"
                style={{ backgroundColor: '#19DF02' }}
              >
                <div className="relative z-10 pr-2" style={{ width: '58%' }}>
                  <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-black/70">
                    Is this you?
                  </div>
                  <h3 className="mt-1 font-display font-black text-[32px] md:text-[44px] leading-[0.95] tracking-[-0.02em] text-black">
                    You Might Be An<br />Ionic Owner If&hellip;
                  </h3>

                  <ul className="mt-4 space-y-1.5">
                    {OWNER_IF.map((c) => (
                      <li key={c} className="flex items-start gap-2 text-black text-[11.5px] leading-[1.35] font-medium">
                        <CheckCircle2 className="w-[14px] h-[14px] mt-[2px] shrink-0 text-black" strokeWidth={2.5} />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/franchise-apply"
                    data-testid="lets-talk-btn"
                    className="mt-5 inline-flex items-center gap-1.5 bg-black text-white hover:text-[color:var(--green)] font-display text-[13px] tracking-[0.10em] uppercase px-6 py-3 rounded-md shadow-[0_10px_25px_-10px_rgba(0,0,0,0.6)] transition-colors"
                  >
                    Let&rsquo;s Talk <ChevronRight className="w-4 h-4" strokeWidth={3} />
                  </Link>
                </div>

                {/* Bulldog mascot — transparent PNG/WebP; enlarged and
                    intentionally overhanging the bottom-right of the panel. */}
                <img
                  src="/franchise/bulldog.webp?v=7"
                  alt="Ionic bulldog mascot wearing an Ionic Estate Sales branded hoodie"
                  data-testid="owner-if-bulldog-image"
                  className="absolute right-1 md:-right-6 bottom-2 md:-bottom-12 w-[40%] md:w-[72%] max-w-[170px] md:max-w-[420px] h-auto object-contain select-none pointer-events-none border-0 outline-none z-20"
                  style={{ background: 'transparent', border: 0, outline: 'none', boxShadow: 'none' }}
                  draggable={false}
                  loading="lazy"
                />
              </div>

              {/* Path + final CTA */}
              <div id="path" data-testid="path" className="relative">
                <div className="reveal font-mono font-bold text-[11px] tracking-[0.28em] uppercase text-white mb-2">
                  Built On Experience
                </div>
                <h2 className="reveal font-display text-[28px] sm:text-[36px] md:text-[44px] leading-[0.98] tracking-[-0.01em] text-white">
                  Designed For Your <span className="text-[color:var(--green)] drop-shadow-[0_0_16px_rgba(25,223,2,0.4)]">Success</span>
                </h2>

                {/* Steps — mobile: 2-col stacked. Desktop: 7-track grid with
                    dedicated connector tracks between step columns so arrows
                    can NEVER touch/overlap an icon. */}
                <div className="mt-6 md:mt-8">
                  {/* Mobile — 2-col, no connectors */}
                  <div className="md:hidden grid grid-cols-2 gap-y-6">
                    {PATH.map((s, i) => {
                      const Icon = s.icon;
                      return (
                        <div key={s.n} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                          <Icon
                            className="w-6 h-6 text-[color:var(--green)] drop-shadow-[0_0_9px_rgba(25,223,2,0.6)]"
                            strokeWidth={1.5}
                          />
                          <div className="mt-2 font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-white/55">
                            {s.n}
                          </div>
                          <h4 className="font-display text-[15px] tracking-wide text-white">{s.t}</h4>
                          <p className="mt-1 text-white/75 text-[12px] leading-[1.45] max-w-[150px]">{s.body}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop — 7-track grid: step | connector | step | connector | step | connector | step */}
                  <div
                    className="hidden md:grid gap-x-3 lg:gap-x-4"
                    style={{ gridTemplateColumns: '1fr auto 1fr auto 1fr auto 1fr' }}
                  >
                    {PATH.map((s, i) => {
                      const Icon = s.icon;
                      return (
                        <React.Fragment key={s.n}>
                          <div className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                            <Icon
                              className="w-7 h-7 text-[color:var(--green)] drop-shadow-[0_0_9px_rgba(25,223,2,0.6)]"
                              strokeWidth={1.5}
                            />
                            <div className="mt-2 font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-white/55">
                              {s.n}
                            </div>
                            <h4 className="font-display text-[16px] tracking-wide text-white">{s.t}</h4>
                            <p className="mt-1 text-white/75 text-[12px] leading-[1.45] max-w-[150px]">{s.body}</p>
                          </div>
                          {i < PATH.length - 1 && (
                            <div
                              data-testid={`path-connector-${i}`}
                              aria-hidden="true"
                              className="self-start flex items-center pointer-events-none"
                              style={{ marginTop: '14px' }}
                            >
                              <span className="w-8 lg:w-10 h-[2px] bg-[color:var(--green)] shadow-[0_0_8px_rgba(25,223,2,0.85)]" />
                              <svg
                                viewBox="0 0 8 8"
                                width="8"
                                height="8"
                                className="text-[color:var(--green)] -ml-[1px]"
                                style={{ filter: 'drop-shadow(0 0 4px rgba(25,223,2,0.9))' }}
                              >
                                <path d="M0 0 L8 4 L0 8 Z" fill="currentColor" />
                              </svg>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* Final CTA — inline at bottom of path column */}
                <div
                  id="final-cta"
                  data-testid="final-cta"
                  className="reveal mt-8 md:mt-10 pt-6 border-t border-[color:var(--green)]/30 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
                >
                  <div className="flex-1">
                    <h3 className="font-display text-[18px] md:text-[22px] tracking-wide text-white uppercase">
                      Ready To Build Something Of Your Own?
                    </h3>
                    <p className="mt-1 text-white/70 text-[13px] leading-[1.5]">
                      No pressure. No hard sell. Just a conversation about what&rsquo;s next.
                    </p>
                  </div>
                  <a
                    href="mailto:ionicteam@ionicestatesales.com?subject=Ionic%20Franchise%20Inquiry"
                    data-testid="start-conversation-btn"
                    className="inline-flex items-center gap-1.5 bg-[color:var(--green)] text-black hover:bg-white font-display text-[13px] tracking-[0.10em] uppercase px-6 py-3 rounded-md shadow-[0_0_28px_-4px_rgba(25,223,2,0.9)] transition-colors whitespace-nowrap"
                  >
                    Start The Conversation <ChevronRight className="w-4 h-4" strokeWidth={3} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer
        logoSrc="/franchise/franchise-logo.webp?v=2"
        logoWrapped={false}
        logoAlt="Ionic Estate Sales Franchise"
      />

      {/* -------------------------------------------------------------------
          Video modal — mounted only when videoOpen is true so the <video>
          element auto-plays fresh every time. Standard native controls,
          escape key + backdrop click to close, respects viewport aspect. */}
      {videoOpen && (
        <div
          data-testid="franchise-video-modal"
          role="dialog"
          aria-label="Ionic Estate Sales franchise video"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm px-4 py-6"
          onClick={() => setVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-[1100px] aspect-video bg-black rounded-lg overflow-hidden border border-[color:var(--green)]/50 shadow-[0_0_60px_-10px_rgba(25,223,2,0.55)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              data-testid="franchise-video-close"
              aria-label="Close video"
              className="absolute top-3 right-3 z-10 grid place-items-center w-9 h-9 rounded-full bg-black/70 border border-white/20 text-white hover:text-[color:var(--green)] hover:border-[color:var(--green)] transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={2.2} />
            </button>
            <video
              data-testid="franchise-video-player"
              src="/franchise/promo-video.mp4"
              className="w-full h-full object-contain bg-black"
              controls
              autoPlay
              playsInline
              preload="auto"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Franchise;
